/**
 * MandatePrioritiesStack — The core navigation instrument.
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ListOrdered, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useProductSurface } from '../../hooks/useProductSurface';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { connectPriorityToOutcome } from '../../lib/llmMandateService';
import type { MandatePriority, MandatePriorityStatus } from '../../types/mandate';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_BADGE: Record<MandatePriorityStatus, string> = {
  active: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  completed: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  dropped: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};

export default function MandatePrioritiesStack() {
  const navigate = useNavigate();
  const { to } = useProductSurface();
  const userId = useUserStore((s) => s.userId);
  const {
    priorities,
    reranks,
    currentOutcome,
    addPriority,
    updatePriority,
    rerankPriority,
    deletePriority,
    promotePriorityToItem,
    launchStackRankingSession,
  } = useMandateStore();

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addRationale, setAddRationale] = useState('');
  const [addRank, setAddRank] = useState<number>(0);
  const [addingPriority, setAddingPriority] = useState(false);

  // AI connection to the Day 100 outcome — fires once title + rationale are filled
  const [connection, setConnection] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const connectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rerank panel: keyed by priority id
  const [rerankingId, setRerankingId] = useState<string | null>(null);
  const [rerankTarget, setRerankTarget] = useState<number>(1);
  const [rerankSignal, setRerankSignal] = useState('');
  const [reranking, setReranking] = useState(false);

  // History panel
  const [showHistory, setShowHistory] = useState(false);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [launchingSession, setLaunchingSession] = useState(false);

  const activePriorities = priorities.filter((p) => p.status === 'active');

  // Debounced AI connection — fires when title + rationale are both filled
  useEffect(() => {
    if (!showAddForm) return;
    setConnection(null);
    if (connectTimer.current) clearTimeout(connectTimer.current);
    if (!addTitle.trim() || !addRationale.trim() || !currentOutcome?.hypothesis) return;

    setConnecting(true);
    connectTimer.current = setTimeout(async () => {
      try {
        const result = await connectPriorityToOutcome(
          addTitle,
          addRationale,
          currentOutcome.hypothesis,
        );
        setConnection(result);
      } finally {
        setConnecting(false);
      }
    }, 1200);

    return () => {
      if (connectTimer.current) clearTimeout(connectTimer.current);
    };
  }, [addTitle, addRationale, currentOutcome?.hypothesis, showAddForm]);

  function openAddForm() {
    setAddTitle('');
    setAddDesc('');
    setAddRationale('');
    setAddRank(activePriorities.length + 1);
    setConnection(null);
    setShowAddForm(true);
  }

  async function handleAdd() {
    if (!addTitle.trim() || !addRationale.trim() || !addDesc.trim()) return;
    setAddingPriority(true);
    try {
      await addPriority({
        title: addTitle.trim(),
        description: addDesc.trim(),
        rationale: addRationale.trim(),
        rank: addRank || activePriorities.length + 1,
      });
      setShowAddForm(false);
    } finally {
      setAddingPriority(false);
    }
  }

  function openRerank(p: MandatePriority) {
    setRerankingId(p.id);
    setRerankTarget(p.rank);
    setRerankSignal('');
  }

  async function handleRerank(p: MandatePriority) {
    if (!rerankSignal.trim()) return;
    setReranking(true);
    try {
      await rerankPriority(p.id, rerankTarget, rerankSignal.trim());
      setRerankingId(null);
    } finally {
      setReranking(false);
    }
  }

  async function handleStatus(id: string, status: MandatePriorityStatus) {
    await updatePriority(id, { status });
  }

  async function handlePromote(p: MandatePriority) {
    if (!userId || p.platformItemId) return;
    setPromotingId(p.id);
    try {
      await promotePriorityToItem(p.id, userId);
    } finally {
      setPromotingId(null);
    }
  }

  async function handleStackRanking() {
    if (!userId) return;
    setLaunchingSession(true);
    try {
      const { sessionId, error } = await launchStackRankingSession(userId);
      if (sessionId) {
        navigate(to(`/sessions/${sessionId}`));
      } else if (error) {
        console.warn('[MandatePriorities] stack ranking:', error);
      }
    } finally {
      setLaunchingSession(false);
    }
  }

  return (
    <div>
      <PageHeader
        icon={<ListOrdered className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-100"
        title="Priorities Stack"
        subtitle="What matters most — re-evaluated as you learn"
        actions={
          <div className="flex items-center gap-2">
            {activePriorities.length >= 2 && (
              <button
                onClick={handleStackRanking}
                disabled={launchingSession}
                className="border border-primary-200 text-primary-700 hover:bg-primary-50 disabled:opacity-50 font-medium px-4 py-2 text-sm"
              >
                {launchingSession ? 'Starting…' : 'Run stack ranking'}
              </button>
            )}
            <button
              onClick={openAddForm}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm"
            >
              + Add priority
            </button>
          </div>
        }
      />

      {/* Explanation bar */}
      <div className="bg-primary-50 border border-primary-100 p-4 mb-6">
        <p className="text-sm text-primary-800">
          Every re-rank requires a signal: what changed that makes this matter more (or less) than
          before? That discipline is what builds your mandate.
        </p>
      </div>

      {/* Priority list */}
      <div className="space-y-2 mb-6">
        {activePriorities.length === 0 && (
          <p className="text-sm text-[var(--text-ter)] py-4 text-center">
            No priorities yet. Add your first priority above.
          </p>
        )}
        {activePriorities.map((p) => (
          <div key={p.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="flex items-start gap-3">
              {/* Rank badge */}
              <div className="w-8 h-8 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] text-sm font-bold flex items-center justify-center flex-shrink-0">
                {p.rank}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-[var(--text-pri)]">{p.title}</span>
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded ${STATUS_BADGE[p.status]}`}
                  >
                    {p.status}
                  </span>
                </div>
                {p.rationale && (
                  <p className="text-sm text-[var(--text-ter)] line-clamp-1 mb-1">{p.rationale}</p>
                )}
                {p.description && (
                  <p className="text-xs text-[var(--text-ter)]">
                    <span className="font-medium text-[var(--text-ter)]">Changes when:</span>{' '}
                    {p.description}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {p.platformItemId ? (
                  <Link
                    to={to(`/items/${p.platformItemId}`)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 inline-flex items-center gap-1"
                  >
                    Item <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : (
                  <button
                    onClick={() => handlePromote(p)}
                    disabled={promotingId === p.id}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 disabled:opacity-50"
                  >
                    {promotingId === p.id ? 'Promoting…' : 'Promote to item'}
                  </button>
                )}
                <button
                  onClick={() => openRerank(p)}
                  className="text-xs text-[var(--text-ter)] hover:text-primary-600 font-medium px-2 py-1 rounded hover:bg-primary-50"
                >
                  Re-rank
                </button>
                <button
                  onClick={() => handleStatus(p.id, 'completed')}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium px-2 py-1 rounded hover:bg-[var(--slate-dim)]"
                >
                  Complete
                </button>
                <button
                  onClick={() => handleStatus(p.id, 'dropped')}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] font-medium px-2 py-1 rounded hover:bg-[var(--hover-tint)]"
                >
                  Drop
                </button>
                <button
                  onClick={() => deletePriority(p.id)}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--coral)] font-medium px-2 py-1 rounded hover:bg-[var(--coral-dim)]"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Re-rank panel */}
            {rerankingId === p.id && (
              <div className="mt-3 pt-3 border-t border-[var(--rule)]">
                <div className="flex items-start gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                      Move to rank:
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={activePriorities.length}
                      value={rerankTarget}
                      onChange={(e) => setRerankTarget(Number(e.target.value))}
                      className="w-16 border border-[var(--rule)] rounded px-2 py-1 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                      What changed? (the signal) <span className="text-[var(--coral)]">*</span>
                    </label>
                    <textarea
                      value={rerankSignal}
                      onChange={(e) => setRerankSignal(e.target.value)}
                      rows={2}
                      className="w-full border border-[var(--rule)] rounded px-2 py-1 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      placeholder="What new information or event drove this re-rank?"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => handleRerank(p)}
                    disabled={!rerankSignal.trim() || reranking}
                    className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-3 py-1.5 rounded text-xs"
                  >
                    {reranking ? 'Applying...' : 'Apply re-rank'}
                  </button>
                  <button
                    onClick={() => setRerankingId(null)}
                    className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] font-medium px-3 py-1.5 rounded border border-[var(--rule)] hover:bg-[var(--surface)]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Non-active priorities (completed/dropped) */}
        {priorities.filter((p) => p.status !== 'active').length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
              Completed &amp; Dropped
            </h3>
            <div className="space-y-2">
              {priorities
                .filter((p) => p.status !== 'active')
                .map((p) => (
                  <div
                    key={p.id}
                    className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] text-sm font-bold flex items-center justify-center flex-shrink-0">
                        —
                      </div>
                      <span className="text-sm text-[var(--text-sec)] line-through">{p.title}</span>
                      <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded ${STATUS_BADGE[p.status]}`}
                      >
                        {p.status}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Add priority inline form */}
      {showAddForm && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 mb-6">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">Add priority</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Title <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Priority title"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Why does this matter now? <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={addRationale}
                onChange={(e) => setAddRationale(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="The rationale — why now?"
              />
            </div>

            {/* AI connection to Day 100 outcome */}
            {currentOutcome && (connecting || connection) && (
              <div
                className={` border px-4 py-3 transition-all ${
                  connecting ? 'border-[var(--rule)] bg-[var(--surface)]' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
                }`}
              >
                {connecting ? (
                  <p className="text-xs text-[var(--text-ter)] italic">Connecting to your Day 100 outcome…</p>
                ) : (
                  <p className="text-xs text-[var(--mustard)] leading-relaxed">{connection}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                What would change this ranking? <span className="text-[var(--coral)]">*</span>
              </label>
              <p className="text-xs text-[var(--text-ter)] mb-1.5">
                What new information or event would cause this to move up or down?
              </p>
              <textarea
                value={addDesc}
                onChange={(e) => setAddDesc(e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="e.g. If the executive sponsor changes, or if the Q3 deadline moves…"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Rank</label>
              <input
                type="number"
                min={1}
                value={addRank}
                onChange={(e) => setAddRank(Number(e.target.value))}
                className="w-20 border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={handleAdd}
              disabled={!addTitle.trim() || !addRationale.trim() || !addDesc.trim() || addingPriority}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm"
            >
              {addingPriority ? 'Adding...' : 'Add to stack'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Signal history panel */}
      <div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider hover:text-[var(--text-sec)]"
        >
          Re-rank history
          {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>

        {showHistory && (
          <div className="mt-3 space-y-2">
            {reranks.length === 0 ? (
              <p className="text-sm text-[var(--text-ter)]">No re-ranks yet.</p>
            ) : (
              reranks.map((r) => {
                const p = priorities.find((pr) => pr.id === r.priorityId);
                return (
                  <div key={r.id} className="text-sm text-[var(--text-sec)]">
                    <span className="font-medium">
                      Moved &ldquo;{p?.title ?? 'priority'}&rdquo;
                    </span>{' '}
                    from #{r.previousRank ?? '?'} to #{r.newRank ?? '?'} —{' '}
                    <span className="text-[var(--text-ter)]">{r.signal}</span>
                    <span className="text-xs text-[var(--text-ter)] ml-2">
                      {formatDate(r.occurredAt)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
